import { getWinningRosterIds, Match, matchContainsRoster } from "./Match";
import { TrackRound } from "./Track/TrackRound";
import { getDefinedRosterIds } from "model/Roster/Roster";
import { onlyUnique } from "Utility";
import { RoundTime } from "./Track/RoundTime";

export type TrackRounds = { [trackId: string]: TrackRound[] | undefined };
export type TrackTimes = { [trackId: string]: RoundTime[] | undefined };

export const getMatchesRosterIds = (matches: Match[]) => {
  return matches
    .flatMap((m) => getDefinedRosterIds(m.teams.rosters))
    .filter(onlyUnique);
};

export const getMatchesRounds = (matches: Match[], trackIds?: string[]) => {
  const rounds: TrackRound[] = [];
  for (let i = 0; i < matches.length; i++) {
    const matchSets = matches[i].sets;
    for (let s = 0; s < matchSets.length; s++) {
      const set = matchSets[s];
      if (set.type === "TimeAttack") {
        continue;
      }

      for (let t = 0; t < set.tracks.length; t++) {
        const track = set.tracks[t];
        if (
          !trackIds ||
          (!!track.trackId && trackIds.includes(track.trackId))
        ) {
          rounds.push(...track.rounds);
        }
      }
    }
  }

  return rounds;
};

export const getMatchesTrackRoundsById = (
  matches: Match[],
  trackIds?: string[]
) => {
  const trackRounds: TrackRounds = {};
  for (let i = 0; i < matches.length; i++) {
    const matchSets = matches[i].sets;
    for (let s = 0; s < matchSets.length; s++) {
      const set = matchSets[s];
      if (set.type === "TimeAttack") {
        continue;
      }

      for (let t = 0; t < set.tracks.length; t++) {
        const track = set.tracks[t];
        const trackId = track.trackId;
        if (!trackId) {
          continue;
        }

        if (!trackIds || trackIds.includes(trackId)) {
          if (!trackRounds[trackId]) {
            trackRounds[trackId] = [];
          }

          trackRounds[trackId]!.push(...track.rounds);
        }
      }
    }
  }

  return trackRounds;
};

export const getMatchesTrackTimesById = (
  matches: Match[],
  trackIds?: string[]
) => {
  const trackTimes: TrackTimes = {};
  for (let i = 0; i < matches.length; i++) {
    const matchSets = matches[i].sets;
    for (let s = 0; s < matchSets.length; s++) {
      const set = matchSets[s];
      if (set.type === "TimeAttack") {
        continue;
      }

      const tracks = set.tracks;
      for (let t = 0; t < tracks.length; t++) {
        const track = tracks[t];
        const trackId = track.trackId;
        if (!trackId) {
          continue;
        }

        if (!trackIds || trackIds.includes(trackId)) {
          const times = trackTimes[trackId];
          if (!times) {
            trackTimes[trackId] = track.rounds.flatMap((r) => r.times);
          } else {
            times.push(...track.rounds.flatMap((r) => r.times));
          }
        }
      }
    }
  }

  return trackTimes;
};

export const calculateTeamRecord = (matches: Match[], rosterId: string) => {
  var wins = 0;
  var loses = 0;
  for (var i = 0; i < matches.length; i++) {
    const match = matches[i];
    if (!matchContainsRoster(match, rosterId)) {
      continue;
    }

    const winnerIds = getWinningRosterIds(match);
    if (winnerIds === undefined) {
      continue;
    }

    if (winnerIds.includes(rosterId)) {
      wins++;
    } else {
      loses++;
    }
  }

  return { wins, loses };
};

export const splitMatchesByDay = (matches: Match[]) => {
  const matchesByDate = new Map<
    { day: number; monthIdx: number; year: number },
    Match[]
  >();
  matches.forEach((m) => {
    const date = new Date(m.startTimeMs);
    const day = date.getDate();
    const monthIdx = date.getMonth();
    const year = date.getFullYear();

    const key = Array.from(matchesByDate.keys()).find(
      (k) => k.day === day && k.monthIdx === monthIdx && k.year === year
    );
    if (!!key) {
      matchesByDate.get(key)!.push(m);
    } else {
      matchesByDate.set({ day, monthIdx, year }, [m]);
    }
  });

  return matchesByDate;
};
